import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useFirestore } from 'reactfire';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { doc, Timestamp, getDoc } from 'firebase/firestore';

import { AnyObjectSchema } from 'yup';
import {
  useNotification,
  useCurrentUser,
  useCompany,
  useQuote,
  usePageTitle,
  useDownloadFiles,
  useForm,
  useQuoteDetails,
  useQuoteMarkets,
  useAuthentication,
} from '../../hooks';
import { Company, FirebaseFile, Quote as QuoteModel } from '../../models';
import { getPageTitle } from '../../utils/pages';
import {
  AdminQuoteProductsTable,
  QuestionDrawer,
  QuoteHeader,
  QuoteProductsTable,
  QuoteSubmit,
  QuoteSummaryDetails,
} from '../../components';
import {
  CompanyType,
  MeasurementUnits,
  ModificationMode,
  NotificationLevel,
  PurchaseStatus,
  QuoteStatus,
  QuoteTypeOfBuy,
  UserRoleCategoryPath,
} from '../../enums';
import getQuoteSchema from '../../validation/QuoteValidations';
import { PricedQuoteStatuses } from '../../mappings';
import { Nullable } from '../../types';
import { getLanguage } from '../../utils/locale';
import { QuoteLayout } from '../../layouts';
import { isUsCompany } from '../../utils';

const Quote = () => {
  const { t, i18n } = useTranslation();
  const firestore = useFirestore();
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const { addNotification } = useNotification();
  const [showSendQuoteConfirm, setShowSendQuoteConfirm] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const currentUser = useCurrentUser();
  const { userRoleCategory } = useAuthentication();
  const { item: currentCompany } = useCompany(currentUser.companyRef.id);
  const pathPrefix = UserRoleCategoryPath[userRoleCategory];
  const quote = useQuote(quoteId || '');

  const isNewQuote = quoteId === 'new';

  usePageTitle(
    quoteId === 'new' ? getPageTitle('quote.new') : getPageTitle('quote.edit')
  );

  const { downloadFromFireStorage } = useDownloadFiles();

  const backToQuotes = `${pathPrefix}/quotes`;

  const form = useForm(quote, {
    mode: isNewQuote ? ModificationMode.Add : ModificationMode.Edit,
    defaultValue: {
      ...new QuoteModel(
        currentUser,
        currentCompany?.type === CompanyType.Agency ? currentCompany : null,
        null,
        currentCompany?.type === CompanyType.CorporateCompany
          ? currentCompany
          : null
      ),
    },
    beforeSave: (dirtyQuote) => {
      const now = Timestamp.now();

      if (currentUser.name && currentUser.email) {
        dirtyQuote.customerId = currentUser.id;
        dirtyQuote.customerName = currentUser.name;
        dirtyQuote.customerEmail = currentUser.email;
      }

      if (isNewQuote) {
        dirtyQuote.dateCreated = now;
        dirtyQuote.status = dirtyQuote.status ?? QuoteStatus.Draft;
      }
      if (
        !dirtyQuote.typeOfBuy ||
        dirtyQuote.typeOfBuy === QuoteTypeOfBuy.InventoryBuy
      ) {
        dirtyQuote.projectName = null;
        dirtyQuote.contractorName = null;
        dirtyQuote.shippingYearMonth = null;
        dirtyQuote.nciProjectId = null;
      }
      dirtyQuote.measurementUnits = units;
      dirtyQuote.lastModified = now;

      if (dirtyQuote.status === QuoteStatus.Submitted) {
        dirtyQuote.dateSubmitted = now;
      }

      if (isQuotePriced(dirtyQuote)) {
        const allWon = dirtyQuote.products.every(
          (p) => p.purchaseStatus === PurchaseStatus.Won
        );
        const allLost = dirtyQuote.products.every(
          (p) => p.purchaseStatus === PurchaseStatus.Lost
        );
        const someWon = dirtyQuote.products.some(
          (p) => p.purchaseStatus === PurchaseStatus.Won
        );

        const somePending = dirtyQuote.products.some(
          (p) =>
            !p.purchaseStatus || p.purchaseStatus === PurchaseStatus.Pending
        );

        if (allWon) {
          dirtyQuote.status = QuoteStatus.Won;
        } else if (someWon) {
          dirtyQuote.status = somePending
            ? QuoteStatus.PartiallyWonPending
            : QuoteStatus.PartiallyWon;
        } else if (allLost) {
          dirtyQuote.status = QuoteStatus.Lost;
        } else if (
          dirtyQuote.expirationTime?.toDate() &&
          dirtyQuote.expirationTime?.toDate() < now.toDate()
        ) {
          dirtyQuote.status = QuoteStatus.Expired;
        } else {
          dirtyQuote.status = QuoteStatus.Quoted;
        }
      }
    },
    onSaveSuccess: (savedQuote) => {
      addNotification(
        !savedQuote.status || savedQuote.status === QuoteStatus.Draft
          ? t('quotes.edit.drawer.save.successDraft')
          : isQuotePriced(savedQuote)
          ? t('quotes.edit.drawer.save.success')
          : t('quotes.edit.drawer.save.successRequestSent'),
        NotificationLevel.Success
      );

      if (isNewQuote && savedQuote.status === QuoteStatus.Draft) {
        navigate(`${backToQuotes}/${savedQuote.id}`, {
          replace: true,
        });
      } else if (
        savedQuote.status === QuoteStatus.Submitted &&
        showSendQuoteConfirm
      ) {
        navigateBackToQuotes();
      }
    },
  });

  const { markets } = useQuoteMarkets(form);
  const { isCurrentTab, isReadonly } = useQuoteDetails(form);

  const [units, setUnits] = useState<MeasurementUnits>(
    currentUser.measurementUnits
  );

  const [priceUnits, setPriceUnits] = useState<MeasurementUnits>(
    company && isUsCompany(company)
      ? MeasurementUnits.Imperial
      : MeasurementUnits.Metric
  );

  const { saveSchema, submitSchema } = getQuoteSchema(markets, units);

  useEffect(() => {
    (async () => {
      if (!form.item?.companyId) {
        setCompany(null);
      } else if (company?.id !== form.item?.companyId) {
        const companyRef = doc(firestore, 'Companies', form.item?.companyId);
        const company = (await getDoc(companyRef)).data();
        setCompany(company as Company);
      }
    })();
  }, [company?.id, firestore, form.item?.companyId]);

  useEffect(() => {
    if (form.item?.measurementUnits) {
      setUnits(form.item?.measurementUnits);
    }

    if (form.item?.priceUnits) {
      setPriceUnits(form.item?.priceUnits);
    }
  }, [form.item?.measurementUnits, form.item?.priceUnits]);

  const saveAsDraft = async () => {
    const valid = await validateQuote(saveSchema);

    if (valid) {
      form.save(true);
    }
  };

  const sendQuoteRequest = async () => {
    const valid = await validateQuote(submitSchema);

    if (valid) {
      setShowSendQuoteConfirm(true);
    }
  };

  const validateQuote = async (schema: AnyObjectSchema) => {
    const errors = await form.validate(
      {
        ...form.item,
        status: QuoteStatus.Submitted,
      } as QuoteModel,
      false,
      true,
      schema
    );

    const errorsObj = Object.entries(errors);
    if (errorsObj.length) {
      if (errorsObj[0][1] === t('common.validations.fieldRequired')) {
        addNotification(
          t('quotes.validation.requiredFields'),
          NotificationLevel.Error
        );
      } else {
        addNotification(t(errorsObj[0][1]), NotificationLevel.Error);
      }
    }

    return errorsObj.length === 0;
  };

  const confirmSendQuoteRequest = async () => {
    if (quoteId && form.item) {
      // Need to assign the value directly for the save action to use the updated property
      form.item.status = QuoteStatus.Submitted;
      form.save();
    }
  };

  const cancelSendQuoteRequest = () => {
    setShowSendQuoteConfirm(false);
  };

  const navigateBackToQuotes = () => {
    const isCurrentQuote = isNewQuote || isCurrentTab || !quote.item;

    navigate(backToQuotes + (!isCurrentQuote ? '/history' : ''));
  };

  const downloadQuote = async () => {
    const file = quote.item?.reportFile?.[getLanguage(i18n)] as FirebaseFile;
    downloadFromFireStorage(
      `${file.storageBucket}/${file.storageId}`,
      file.fileName
    );
  };

  const isQuotePriced = useCallback(
    (quote: Nullable<QuoteModel>) =>
      Boolean(
        quote?.products?.length && PricedQuoteStatuses.includes(quote.status)
      ),
    []
  );

  const getHeaderElement = () => (
    <QuoteHeader
      form={form}
      isNewQuote={isNewQuote}
      onSave={saveAsDraft}
      navigateBack={navigateBackToQuotes}
      saveButtonText={
        isQuotePriced(form.item)
          ? t('quotes.edit.saveQuote')
          : t('quotes.edit.saveAsDraft')
      }
      showSaveButton={
        form.dirty &&
        (isNewQuote ||
          form.item?.status === QuoteStatus.Draft ||
          isQuotePriced(form.item))
      }
      units={units}
      setUnits={setUnits}
    />
  );

  const getBodyElement = () => (
    <>
      <QuoteSummaryDetails form={form} isReadonly={isReadonly} />
      {isQuotePriced(form.item) ? (
        <AdminQuoteProductsTable
          company={company}
          form={form}
          units={units}
          setUnits={setUnits}
          priceUnits={priceUnits}
          isReadonly
        />
      ) : (
        <QuoteProductsTable
          company={company}
          form={form}
          units={units}
          priceUnits={priceUnits}
          setUnits={setUnits}
        />
      )}
    </>
  );

  const getFooterElement = () => (
    <QuoteSubmit
      company={company}
      form={form}
      units={units}
      priceUnits={priceUnits}
      submitQuote={sendQuoteRequest}
      downloadQuote={downloadQuote}
    />
  );

  return (
    <>
      <QuoteLayout
        headerElement={getHeaderElement()}
        bodyElement={getBodyElement()}
        footerElement={getFooterElement()}
      />
      <QuestionDrawer
        open={showSendQuoteConfirm}
        icon={<RequestQuoteIcon sx={{ color: 'secondary.main' }} />}
        message={t('quotes.edit.summary.submitQuoteRequestDescription')}
        onConfirm={confirmSendQuoteRequest}
        onCancel={cancelSendQuoteRequest}
        title={t('quotes.edit.summary.submitQuoteRequestButtonLabel')}
      />
    </>
  );
};

export default Quote;
